// 📄 pages/Proyectos/components/EspecificacionesTecnicasSection.tsx
// "Especificaciones Técnicas" (Funcionalidades): documentos que se
// completan entre el área Funcional y Técnica — ver
// especificacionTecnicaService.ts para el detalle de diseño.
import React, { useEffect, useMemo, useState } from 'react';
import { clasificacionDe } from '../modulosConfig.ts';
import { FileCode2, Plus, RefreshCw } from 'lucide-react';
import { useProject } from '../../../context/ProjectContext.tsx';
import {
  EspecificacionTecnica, EspecificacionEstado, ESPECIFICACION_ESTADOS,
  getEspecificaciones, updateEspecificacionEstado
} from '../../../services/especificacionTecnicaService.ts';
import { SOLUCIONES } from '../solucionesConfig.ts';
import { canalesDe } from './CasoPruebaFormModal.tsx';
import EspecificacionTecnicaCard from './EspecificacionTecnicaCard.tsx';
import EspecificacionTecnicaFormModal from './EspecificacionTecnicaFormModal.tsx';

export default function EspecificacionesTecnicasSection() {
  const { project, projectId, esCliente, esAdminObservador } = useProject();
  const [especificaciones, setEspecificaciones] = useState<EspecificacionTecnica[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState<EspecificacionEstado | 'todos'>('todos');
  const [filtroSolucion, setFiltroSolucion] = useState('todas');
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState<EspecificacionTecnica | null>(null);

  // Con SAP Ariba se clasifica por solución; sin SAP, por área del proyecto (ver modulosConfig.ts).
  const { modoArea, opciones: solucionesActivas } = clasificacionDe(project);
  const puedeEditar = !esCliente && !esAdminObservador;

  const cargar = async () => {
    setLoading(true);
    try {
      setEspecificaciones(await getEspecificaciones(projectId));
    } catch (err) {
      console.error('Error cargando especificaciones técnicas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const opcionesCanalFiltro = useMemo(
    () => (filtroSolucion === 'todas' ? [] : canalesDe(filtroSolucion)),
    [filtroSolucion]
  );

  const filtradas = useMemo(() => {
    return especificaciones.filter((e) => {
      if (filtroEstado !== 'todos' && e.estado !== filtroEstado) return false;
      if (filtroSolucion !== 'todas' && e.solucionRelacionada !== filtroSolucion) return false;
      if (filtroCanal !== 'todos' && (e.canalId || '') !== filtroCanal) return false;
      return true;
    });
  }, [especificaciones, filtroEstado, filtroSolucion, filtroCanal]);

  const handleEstadoChange = async (id: string, estado: EspecificacionEstado) => {
    setEspecificaciones((prev) => prev.map((e) => (e.id === id ? { ...e, estado } : e)));
    try {
      await updateEspecificacionEstado(projectId, id, estado);
    } catch (err) {
      console.error('Error actualizando estado de la especificación:', err);
      cargar();
    }
  };

  const handleSaved = () => {
    setFormOpen(false);
    setEditando(null);
    cargar();
  };

  if (!project) return null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value as EspecificacionEstado | 'todos')}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todos">Todos los estados</option>
            {ESPECIFICACION_ESTADOS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>

          <select
            value={filtroSolucion}
            onChange={(e) => { setFiltroSolucion(e.target.value); setFiltroCanal('todos'); }}
            className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
          >
            <option value="todas">{modoArea ? 'Todas las áreas' : 'Todas las soluciones'}</option>
            {solucionesActivas.map((s) => <option key={s} value={s}>{SOLUCIONES[s]?.label || s}</option>)}
          </select>

          {opcionesCanalFiltro.length > 0 && (
            <select
              value={filtroCanal}
              onChange={(e) => setFiltroCanal(e.target.value)}
              className="bg-zinc-800 border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:outline-none"
            >
              <option value="todos">Todos los canales</option>
              {opcionesCanalFiltro.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          )}
        </div>

        {puedeEditar && (
          <button
            onClick={() => { setEditando(null); setFormOpen(true); }}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-xl text-sm font-semibold text-white transition-all"
          >
            <Plus className="w-4 h-4" /> Nueva especificación
          </button>
        )}
      </div>

      {loading ? (
        <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        </div>
      ) : filtradas.length === 0 ? (
        <div className="p-10 text-center text-gray-500 flex flex-col items-center gap-2">
          <FileCode2 className="w-8 h-8 text-gray-600" />
          <p className="text-sm">Todavía no hay especificaciones técnicas{(filtroSolucion !== 'todas' || filtroEstado !== 'todos') ? ' para este filtro' : ''}.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtradas.map((e) => (
            <EspecificacionTecnicaCard
              key={e.id}
              especificacion={e}
              puedeEditar={puedeEditar}
              onEditar={() => { setEditando(e); setFormOpen(true); }}
              onEstadoChange={(estado) => handleEstadoChange(e.id, estado)}
            />
          ))}
        </div>
      )}

      {formOpen && (
        <EspecificacionTecnicaFormModal
          projectId={projectId}
          solucionesActivas={solucionesActivas}
          modoArea={modoArea}
          especificacion={editando}
          onClose={() => { setFormOpen(false); setEditando(null); }}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
